import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { DocumentSummary, SignatureEnvelopeSummary } from '../types';

/**
 * "Send for signature" modal. Posts to /api/documents/:id/send-for-signature.
 * Handles the special-case "consent_required" response — shows a CTA that
 * pops the DocuSign consent URL in a new tab.
 */
export function SignatureModal({
  document: doc,
  onSent,
  onClose,
}: {
  document: DocumentSummary;
  onSent: (envelope: SignatureEnvelopeSummary) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['signatures', 'common']);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentUrl, setConsentUrl] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setConsentUrl(null);
    setBusy(true);
    try {
      const env = await apiFetch<SignatureEnvelopeSummary>(
        `/api/documents/${doc.id}/send-for-signature`,
        {
          method: 'POST',
          body: JSON.stringify({
            recipientEmail: recipientEmail.trim(),
            recipientName: recipientName.trim(),
            subject: subject.trim() || undefined,
            message: message.trim() || undefined,
          }),
        },
      );
      onSent(env);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'consent_required') {
        const url = (err.body as { consentUrl?: string } | null)?.consentUrl;
        if (url) setConsentUrl(url);
        setError(t('errors.consent_required'));
      } else if (err instanceof ApiError) {
        const key = `errors.${err.code}`;
        const translated = t(key);
        setError(translated === key ? t('common:errors.code', { code: err.code }) : translated);
      } else {
        setError(err instanceof Error ? err.message : t('common:errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">{t('modal.title')}</div>
              <div className="form-preview-type">{doc.name}</div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('modal.recipient_name')} <span className="req">*</span></label>
                <input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  required
                  minLength={2}
                  placeholder={t('modal.recipient_name_placeholder')}
                  autoFocus
                />
              </div>
              <div className="field">
                <label>{t('modal.recipient_email')} <span className="req">*</span></label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  placeholder={t('modal.recipient_email_placeholder')}
                />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('modal.subject')}</label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('modal.subject_placeholder')}
                />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('modal.message')}</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('modal.message_placeholder')}
                />
              </div>
            </div>
          </div>

          {consentUrl && (
            <div className="form-section">
              <div className="signin-error" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {t('modal.consent_required_title')}
                </div>
                <div style={{ marginBottom: 8 }}>{t('modal.consent_required_body')}</div>
                <a href={consentUrl} target="_blank" rel="noopener noreferrer" className="btn-link">
                  {t('modal.consent_required_cta')} →
                </a>
              </div>
            </div>
          )}

          {error && !consentUrl && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('modal.submit_busy') : t('modal.submit')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
